import type { CelebrationTemplateData } from '@om/shared-types';

/** All templates for a celebration type. */
export function getTemplatesForType(templates: CelebrationTemplateData[], type: string): CelebrationTemplateData[] {
  return templates.filter((template) => template.type === type);
}

/**
 * Best match for type + subtype: the exact subtype template, otherwise the
 * type's only template. Returns undefined when the choice is ambiguous so the
 * user picks (or proceeds without a template).
 */
export function getBestMatchTemplate(
  templates: CelebrationTemplateData[],
  type: string,
  subtype?: string | null,
  culturalHints: Array<string | null | undefined> = [],
): CelebrationTemplateData | undefined {
  const ofType = getTemplatesForType(templates, type);
  if (subtype) {
    const sameSubtype = ofType.filter((template) => template.subtype === subtype);
    const hints = culturalHints.filter((hint): hint is string => Boolean(hint)).map((hint) => hint.toLowerCase());
    if (hints.length > 0) {
      const byHint = sameSubtype.find((template) =>
        ['Religion', 'Cultural'].some((category) =>
          (template.suggestedTags[category] ?? []).some((value) => hints.includes(value.toLowerCase())),
        ),
      );
      if (byHint) return byHint;
    }
    const canonical = sameSubtype.find((template) => template.key === `${type}_${subtype}`);
    if (canonical ?? sameSubtype[0]) return canonical ?? sameSubtype[0];
  }
  return ofType.length === 1 ? ofType[0] : undefined;
}

/**
 * A type is single-event for the Quick section when at least as many of its
 * templates have one (or zero) events as have more than one. Types without
 * templates default to single-event.
 */
export function isSingleEventType(templates: CelebrationTemplateData[], type: string): boolean {
  const ofType = getTemplatesForType(templates, type);
  if (ofType.length === 0) return true;

  const singleCount = ofType.filter((template) => template.suggestedEvents.length <= 1).length;
  return singleCount >= ofType.length - singleCount;
}
